import { $insertGeneratedNodes } from '@lexical/clipboard';
import { $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import { mergeRegister, objectKlassEquals } from '@lexical/utils';
import {
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  BaseSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  isDOMNode,
  isSelectionCapturedInDecoratorInput,
  LexicalEditor,
  PASTE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

import usePasteTarget from '../../hooks/usePasteTarget';
import {
  capturePastedPictures,
  displaySize,
  isPictureFile,
  picturesFromFiles,
  reportNotAdded,
  reportUnpasted,
} from '../../utils/clipboardPictures';
import { INormalizedImage } from '../../utils/normalizeImage';
import { INSERT_IMAGE_COMMAND } from '../ImagesPlugin';

/*
  Pictures into the compose Body: a pasted screenshot, a dropped PNG or JPEG, and the pictures in a
  paste from Word or classic Outlook.

  Every one goes through normalizeImage on the way in (see clipboardPictures), so what the editor
  holds is already what gets sent - sized to fit, as a data: URI the send swaps for a cid:
  attachment (inlineImages.ts). That takes a moment, so a paste is tied to the message it was made
  in, for an editor that is re-pointed at others (a Batch Send row's) - see usePasteTarget.

  Only the compose editor mounts this. The simple editor behind the settings templates (Email
  Signature and the like) has no way to send a picture, so it takes none.
*/

function insertPictures(editor: LexicalEditor, pictures: INormalizedImage[]) {
  pictures.forEach((image) =>
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
      altText: '',
      src: image.src,
      ...displaySize(image),
      captionsEnabled: false,
    })
  );
}

// Where the paste lands: the selection as it is now, or failing that as it was when the sender
// pasted - the pictures took a moment, and a click in the meantime can take the selection away.
function $pasteSelection(saved: BaseSelection | null): BaseSelection {
  const current = $getSelection();
  if (current) return current;

  if (
    $isRangeSelection(saved) &&
    $getNodeByKey(saved.anchor.key) !== null &&
    $getNodeByKey(saved.focus.key) !== null
  ) {
    $setSelection(saved);
    const restored = $getSelection();
    if (restored) return restored;
  }

  return $getRoot().selectEnd();
}

function insertDocument(editor: LexicalEditor, dom: Document, saved: BaseSelection | null) {
  editor.update(
    () => {
      const selection = $pasteSelection(saved);
      // The same two steps Lexical's own paste takes, run on the document with its pictures filled in.
      $insertGeneratedNodes(editor, $generateNodesFromDOM(editor, dom), selection);
    },
    { tag: 'paste' }
  );
}

interface Props {
  // Which message the editor holds, when it is re-pointed at others - see usePasteTarget.
  contentKey?: string;
}

export default function ImagePastePlugin({ contentKey }: Props): null {
  const [editor] = useLexicalComposerContext();
  const notePasteTarget = usePasteTarget(contentKey);

  useEffect(
    () =>
      mergeRegister(
        // A dropped PNG or JPEG: Lexical's rich text plugin raises this with the dropped files.
        editor.registerCommand(
          DRAG_DROP_PASTE,
          (files) => {
            const images = files.filter(isPictureFile);
            if (!images.length) return false;

            const stillOpen = notePasteTarget();

            picturesFromFiles(images).then(({ pictures, unpasted }) => {
              if (stillOpen()) insertPictures(editor, pictures);
              else reportNotAdded(pictures.length, 'Drop');

              reportUnpasted(unpasted);
            });

            return true;
          },
          COMMAND_PRIORITY_LOW
        ),

        /*
          A paste with pictures in it: a screenshot, "Copy image", or HTML naming pictures. Taken
          ahead of Lexical's own paste only when there is a picture to deal with - without one
          (words, Excel cells) this steps aside and the paste goes exactly as it always has.
        */
        editor.registerCommand(
          PASTE_COMMAND,
          (event) => {
            if (!objectKlassEquals(event, ClipboardEvent)) return false;

            // Typing into an input inside a decorator (a table's Layout select) is that input's.
            if (isDOMNode(event.target) && isSelectionCapturedInDecoratorInput(event.target)) {
              return false;
            }

            const { clipboardData } = event as ClipboardEvent;

            // Copied from one of these editors: its pictures are already sized data: URIs.
            if (clipboardData?.types.includes('application/x-lexical-editor')) return false;

            const capture = capturePastedPictures(clipboardData);
            if (!capture) return false;

            const saved = editor.getEditorState().read(() => $getSelection()?.clone() ?? null);
            const stillOpen = notePasteTarget();

            event.preventDefault();

            capture().then(({ dom, pictures, unpasted }) => {
              if (!stillOpen()) reportNotAdded(dom ? 'paste' : pictures.length);
              else if (dom) insertDocument(editor, dom, saved);
              else insertPictures(editor, pictures);

              reportUnpasted(unpasted);
            });

            return true;
          },
          COMMAND_PRIORITY_HIGH
        )
      ),
    [editor, notePasteTarget]
  );

  return null;
}
